<template>
  <f7-page class="chat" @page:beforein="pageBeforein" @page:beforeout="pageBeforeout">
    <f7-navbar v-if="lesson && lesson.user"> </f7-navbar>

    <span v-if="lesson && !lesson.messages.length" class="chat-empty-message">
      Start chatting with {{ lesson.user.name }}
    </span>

    <f7-messagebar
      ref="messagebar"
      :placeholder="placeholder"
      :attachments-visible="attachmentsVisible"
      @input="handleInputEvent"
      @blur="hideTyping"
      @keydown.enter.native.exact.prevent
      @keyup.enter.native.exact="send"
      @keydown.enter.shift.native.exact
    >
      <label
        v-if="!selectedMessage.text && !selectedMessage.info"
        slot="inner-start"
        class="chat-attach-file ripple"
      >
        <f7-icon f7="camera_fill" color="blue" size="36"></f7-icon>
        <f7-input
          type="file"
          :accept="ACCEPT_FILE_TYPE"
          multiple
          @change="updateAttachments"
        ></f7-input>
      </label>

      <f7-link
        v-if="!voiceMessage"
        slot="inner-end"
        :class="['ripple', { recording: isRecord }]"
        :icon-f7="isRecord ? 'pause_round_fill' : 'mic_round_fill'"
        icon-color="blue"
        icon-size="40"
        @click="record"
      >
        <div class="recordVawe"></div>
      </f7-link>

      <f7-link
        v-if="voiceMessage"
        slot="inner-end"
        icon-f7="close_round_fill"
        icon-color="red"
        icon-size="40"
        @click="voiceMessage = null"
      ></f7-link>

      <!-- <div slot="after-area" v-if="voiceMessage">
        Voice message {{ voiceMessage.duration.minutes | two_digits }}:{{
          voiceMessage.duration.seconds | two_digits
        }}s
      </div> -->

      <div
        v-if="selectedMessage.id && selectedMessage.text"
        slot="before-area"
        class="quote-preview"
      >
        <f7-link
          icon-f7="close"
          icon-size="24"
          class="quote-preview-close"
          @click="selectedMessage = { id: null, text: '', info: '' }"
        ></f7-link>
        <img src="@/static/quote.svg" alt="quote" />
        <p class="quote-preview-text">{{ selectedMessage.text }}</p>
        <div class="quote-preview-info">{{ selectedMessage.info }}</div>
      </div>

      <f7-link
        slot="inner-end"
        class="ripple"
        icon-f7="arrow_up_round_fill"
        icon-color="blue"
        icon-size="40"
        @click="send"
      ></f7-link>

      <f7-messagebar-attachments>
        <f7-messagebar-attachment
          v-for="(attachment, index) in attachments"
          :key="index"
          :image="attachment.preview"
          @attachment:delete="attachments.splice(index, 1)"
        ></f7-messagebar-attachment>

        <f7-messagebar-attachment class="messagebar-attachment-add-button ripple">
          <label>
            <f7-icon f7="add" color="white" size="35"></f7-icon>
            <f7-input
              type="file"
              :accept="ACCEPT_FILE_TYPE"
              multiple
              @change="updateAttachments"
            ></f7-input>
          </label>
        </f7-messagebar-attachment>
      </f7-messagebar-attachments>
    </f7-messagebar>

    <f7-messages ref="messages">
      <f7-message
        v-for="(message, index) in lesson && lesson.messages ? lesson.messages : []"
        :key="index"
        :type="message.type"
        :avatar="message.type === 'received' ? `${BASE_PATH}${lesson.user.avatar}` : ''"
        :first="isFirstMessage(message, index)"
        :last="isLastMessage(message, index)"
        :tail="isTailMessage(message, index)"
        :class="{ 'with-mark': message.mark }"
      >
        <span v-if="message.text" slot="text" v-html="message.text"></span>

        <span v-if="message.isQuestion && message.answers" slot="default" class="question-button">
          <template v-for="(answer, index2) in message.answers">
            <f7-button v-if="answer.type === 'text'" :key="index2" fill large :active="false">
              {{ answer.text }}
            </f7-button>

            <img
              v-if="answer.type === 'image'"
              :key="index2"
              :src="`${BASE_PATH}/storage/${answer.image}`"
              alt="alt"
            />
          </template>
        </span>

        <!-- QUOTE TEMPLATE -->
        <span v-if="message.quote" slot="bubble-start" class="quote-messages">
          <img src="../static/quote-white.svg" alt="quote" />
          <div class="quote-messages-text">{{ message.quote }}</div>
          <div class="quote-messages-info">
            {{ format(new Date(message.quoteDate), 'd.L.yyyy, HH:mm:ss') }}
          </div>
        </span>

        <!-- MARK TEMPLATE -->
        <span v-if="message.mark" slot="bubble-end" class="message-mark-icon">
          {{ message.mark === 'like' ? '👍' : '👎' }}
        </span>

        <!-- IMAGE TEMPLATE -->
        <span v-if="message.attachment && message.attachment.type === 'image'" slot="image">
          <img
            :src="
              `${message.attachment.type === 'image_url' ? '' : BASE_PATH}${message.attachment.src}`
            "
            :alt="message.attachment.name"
            @click="openPhotoBrowser(message.attachment.src)"
          />
        </span>

        <span v-if="message.attachment && message.attachment.type === 'image_url'" slot="image">
          <img
            :src="message.attachment.src"
            :alt="message.attachment.name"
            @click="openPhotoBrowser(message.attachment.src)"
          />
        </span>

        <!-- AUDIO MESSAGE TEMPLATE -->
        <span v-if="message.attachment && message.attachment.type === 'audio'" slot="default">
          <AudioPlayer
            :src="`${BASE_PATH}${message.attachment.src}`"
            :type="message.type"
          ></AudioPlayer>
        </span>

        <!-- FILE TEMPLATE -->
        <span
          v-if="
            message.attachment &&
              message.attachment.type !== 'image' &&
              message.attachment.type !== 'audio' &&
              message.attachment.type !== 'image_url'
          "
          slot="default"
        >
          <FileCard
            :name="message.attachment.name"
            :src="message.attachment.src"
            :size="fileSize(message.attachment.size)"
            :type="message.attachment.type"
          ></FileCard>
        </span>

        <!-- DATE TEMPLATE -->
        <span slot="end" class="message-time">
          {{ format(new Date(message.date), 'HH:mm:ss') }}
        </span>

        <!-- MESSAGE UTILS BUTTON -->
        <f7-link
          v-if="message.type === 'received'"
          slot="start"
          class="message-utils-button"
          icon-f7="more_vertical_fill"
          icon-color="blue"
          popover-open=".message-utils-popover"
          @click="selectedMessage.id = message.id"
        ></f7-link>
      </f7-message>

      <f7-message
        v-if="typing"
        type="received"
        :typing="true"
        :first="true"
        :last="true"
        :tail="true"
        :avatar="`${BASE_PATH}${lesson.user.avatar}`"
      ></f7-message>
    </f7-messages>

    <f7-popover
      class="message-utils-popover"
      @popover:closed="
        selectedMessage.text && selectedMessage.info
          ? null
          : (selectedMessage = { id: null, text: '', info: '' })
      "
    >
      <f7-list no-chevron>
        <f7-list-item
          link="#"
          popover-close
          title="Like"
          @click="markMessage('like')"
        ></f7-list-item>

        <f7-list-item
          link="#"
          popover-close
          title="Dislike"
          @click="markMessage('dislike')"
        ></f7-list-item>

        <f7-list-item link="#" popover-close title="Quote" @click="quoteMessage"></f7-list-item>
      </f7-list>
    </f7-popover>
  </f7-page>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { format } from 'date-fns';
import MicRecorder from 'mic-recorder-to-mp3';

// import { SET_CURRENT_CHAT_ID } from '@/store/mutations.js';
import { BASE_PATH, ACCEPT_FILE_TYPE, NOTIFICATION_DURATION } from '@/js/constants/index.js';

import apiRequest from '@/js/helpers/apiRequest.js';
import notify from '@/js/helpers/notify.js';
import filePreview from '@/js/helpers/filePreview.js';
import fileSize from '@/js/helpers/fileSize.js';
import getAudioDuration from '@/js/helpers/getAudioDuration.js';

import FileCard from '@/components/FileCard.vue';
import AudioPlayer from '@/components/AudioPlayer.vue';

export default {
  name: 'Chat',

  components: {
    FileCard,
    AudioPlayer
  },

  data() {
    return {
      lessonId: Number(this.$f7route.params.id),
      attachments: [],
      voiceMessage: null,
      recorder: null,
      audio: null,
      isRecord: false,
      typing: null,
      timerId: null,
      photoBrowserId: null,
      now: Math.trunc(new Date().getTime() / 1000),
      selectedMessage: {
        id: null,
        text: '',
        info: ''
      }
    };
  },

  computed: {
    BASE_PATH: () => BASE_PATH,
    ACCEPT_FILE_TYPE: () => ACCEPT_FILE_TYPE,
    fileSize: () => fileSize,
    format: () => format,

    attachmentsVisible() {
      return this.attachments.length > 0;
    },

    placeholder() {
      return this.attachments.length > 0 ? 'Add comment or Send' : 'Message...';
    },

    photos() {
      const photos = this.lesson.messages
        .filter(message => message.attachment && message.attachment.type === 'image')
        .map(message => `${BASE_PATH}${message.attachment.src}`);

      return photos;
    },

    // startInMilliseconds() {
    //   return Math.trunc(new Date(this.lesson.start).getTime() / 1000);
    // },

    // seconds() {
    //   return (this.now - this.startInMilliseconds) % 60;
    // },

    // minutes() {
    //   return Math.trunc((this.now - this.startInMilliseconds) / 60) % 60;
    // },

    ...mapState({
      user: state => state.user.info,
      lesson(state) {
        return state.lesson.lessons.find(lesson => lesson.id === this.lessonId);
      }
    })
  },

  mounted() {
    this.messagebar = this.$refs.messagebar.f7Messagebar;
    this.messages = this.$refs.messages.f7Messages;

    this.recorder = new MicRecorder({
      bitRate: 128
    });
  },

  methods: {
    pageBeforein() {
      this.$$(window).trigger('resize');

      this.subscribeToLessonChannel(this.lessonId);
      this.$store.commit('lesson/SET_CURRENT_CHAT_ID', {
        lessonId: this.lessonId
      });
    },

    pageBeforeout() {
      this.hideTyping();
      this.unsubscribeFromLessonChannel(this.lessonId);
    },

    subscribeToLessonChannel(lessonId) {
      this.$echo.private(`lesson.${lessonId}`).listenForWhisper('typing', res => {
        this.typing = res.typing;
      });
    },

    unsubscribeFromLessonChannel(lessonId) {
      this.$echo.leave(`lesson.${lessonId}`);
    },

    isFirstMessage(message, index) {
      const previousMessage = this.lesson.messages[index - 1];
      if (message.isTitle) return false;
      if (
        !previousMessage ||
        previousMessage.type !== message.type ||
        previousMessage.name !== message.name
      )
        return true;
      return false;
    },

    isLastMessage(message, index) {
      const nextMessage = this.lesson.messages[index + 1];
      if (message.isTitle) return false;
      if (!nextMessage || nextMessage.type !== message.type || nextMessage.name !== message.name)
        return true;
      return false;
    },

    isTailMessage(message, index) {
      const nextMessage = this.lesson.messages[index + 1];
      if (message.isTitle) return false;
      if (!nextMessage || nextMessage.type !== message.type || nextMessage.name !== message.name)
        return true;
      return false;
    },

    markMessage(mark) {
      const { id } = this.selectedMessage;

      try {
        const body = new FormData();
        body.append('message_id', id);
        body.append('mark', mark);

        const res = apiRequest('message/mark', 'POST', body, true);

        if (res.error) {
          throw res.error;
        }
      } catch (error) {
        this.$f7.toast
          .create({
            position: 'bottom',
            destroyOnClose: true,
            closeTimeout: NOTIFICATION_DURATION,
            text: error.message || error
          })
          .open();
      }
    },

    quoteMessage() {
      const message = this.lesson.messages.find(message => message.id === this.selectedMessage.id);

      if (message && message.text) {
        const { text, date } = message;
        const userName = this.lesson.user.name;

        this.selectedMessage.text = text;
        this.selectedMessage.info = `${userName}, ${format(new Date(date), 'd.L.yyyy, HH:mm:ss')}`;
      }
    },

    updateAttachments(e) {
      const { files } = e.target;

      for (let file of files) {
        if (file.type.split('/')[0] === 'image') {
          const reader = new FileReader();

          reader.onload = e => {
            this.attachments.push({
              preview: e.target.result,
              file: file
            });
          };
          reader.readAsDataURL(file);
        } else {
          filePreview({
            name: file.name,
            type: file.type,
            size: fileSize(file.size)
          }).then(preview => {
            this.attachments.push({
              preview,
              file: file
            });
          });
        }
      }
    },

    createPhotoBrowser() {
      this.photoBrowserId = this.$f7.photoBrowser.create({
        theme: 'dark',
        photos: this.photos
      });
    },

    async openPhotoBrowser(src) {
      const index = this.photos.indexOf(`${BASE_PATH}${src}`);

      if (this.photoBrowserId) {
        this.photoBrowserId.destroy();

        await this.$nextTick();
        this.createPhotoBrowser();
        await this.$nextTick();
        this.photoBrowserId.open(index);
      } else {
        this.createPhotoBrowser();
        await this.$nextTick();
        this.photoBrowserId.open(index);
      }
    },

    record() {
      if (!this.isRecord) {
        this.recorder
          .start()
          .then(() => {
            this.isRecord = true;
          })
          .catch(error => {
            console.error(error);
          });
      } else {
        this.recorder
          .stop()
          .getMp3()
          .then(([buffer, blob]) => {
            this.isRecord = false;

            const file = new File(buffer, 'voice-message.mp3', {
              type: blob.type,
              lastModified: Date.now()
            });

            getAudioDuration(blob, duration => {
              const minutes = Math.floor(duration / 60);
              const seconds = duration - minutes * 60;

              this.voiceMessage = {
                file,
                duration: {
                  minutes,
                  seconds
                }
              };
            });
          })
          .catch(error => {
            console.error(error);
          });
      }
    },

    handleInputEvent(e) {
      if (!e.target.value) {
        this.hideTyping();

        return;
      }

      this.showTyping();
    },

    showTyping() {
      this.$echo.private(`lesson.${this.lessonId}`).whisper('typing', {
        typing: true,
        userId: this.user.id
      });
    },

    hideTyping() {
      this.$echo.private(`lesson.${this.lessonId}`).whisper('typing', {
        typing: false
      });
    },

    send() {
      if (this.voiceMessage) {
        this.sendMessage({
          lessonId: this.lesson.id,
          attachment: this.voiceMessage.file
        });

        this.voiceMessage = null;
      }

      const text = this.messagebar
        .getValue()
        .replace(/\n/g, '<br>')
        .trim();

      const messagesToSend = [];

      if (text.trim().length) {
        messagesToSend.push({
          text
        });
      }

      if (this.attachments.length) {
        this.attachments.forEach(attachment => {
          messagesToSend.push({
            file: attachment.file
          });
        });
      }

      if (messagesToSend.length === 0) {
        return;
      }

      messagesToSend.forEach((value, item) => {
        const type = Object.keys(value)[0];

        switch (type) {
          case 'text': {
            try {
              this.sendMessage({
                lessonId: this.lesson.id,
                messageId:
                  this.selectedMessage.id && this.selectedMessage.text
                    ? this.selectedMessage.id
                    : '',
                text: value.text
              });
            } catch (error) {
              if (error.customeError) {
                notify({ text: error.customeError });
              }
            }

            break;
          }

          case 'file': {
            try {
              this.sendMessage({
                lessonId: this.lesson.id,
                messageId:
                  this.selectedMessage.id && this.selectedMessage.text
                    ? this.selectedMessage.id
                    : '',
                attachment: value.file
              });
            } catch (error) {
              if (error.customeError) {
                notify({ text: error.customeError });
              }
            }

            break;
          }
        }

        if (text.length) this.$refs.messagebar.f7Messagebar.focus();
      });

      this.$refs.messagebar.f7Messagebar.clear();
      this.selectedMessage = { id: null, text: '', info: '' };
      this.attachments = [];
      this.hideTyping();
    },

    ...mapActions({
      sendMessage: 'lesson/sendMessage'
    })
  }
};
</script>

<style lang="less">
.chat {
  .navbar-inner {
    padding-left: 0;
  }

  .message {
    // max-width: 100%;

    --f7-message-bubble-padding-vertical: 8px;
    --f7-message-bubble-line-height: 24px;
    --f7-message-bubble-padding-horizontal: 15px;

    &.message-received {
      &:hover,
      &:focus {
        .message-utils-button.link {
          opacity: 1;
          visibility: visible;
        }
      }
    }

    &.with-mark {
      margin-bottom: 25px;

      .message-bubble {
        padding-bottom: 20px;
      }
    }

    .message-mark-icon {
      position: absolute;
      bottom: -12px;
      right: 15px;
      width: 30px;
      height: 30px;
      font-size: 15px;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #32acf5;
      border-radius: 50%;
      box-shadow: 0 2px 3px 0 rgba(210, 210, 210, 1);
    }
  }

  &-subnavbar {
    border-bottom-left-radius: 38px;
    border-bottom: 1px solid #d5d8dd;

    .subnavbar-inner {
      display: flex;
      flex-direction: column;
      padding-bottom: 15px;
    }

    &-info {
      margin-bottom: 10px;
      font-size: 12px;
      letter-spacing: -0.29px;
    }
  }

  .toolbar-inner {
    display: flex;
    padding-top: 13px;
    padding-bottom: 13px;
    border-top: 1px solid #d5d8dd;

    .link {
      align-self: center;

      &:not(:last-of-type) {
        margin-right: 10px;
      }
    }
  }

  .messagebar-area {
    margin: 5px 20px !important;

    --f7-messagebar-textarea-padding: 10px 15px 10px;

    + a.link {
      margin-left: 0 !important;
    }
  }

  .message-time {
    font-weight: 300;
    font-size: 16px;
    color: #d5d8dd;
  }

  .message-image {
    margin: calc(var(--f7-message-bubble-padding-vertical) - 30px)
      calc(-1 * var(--f7-message-bubble-padding-horizontal) + 0px) !important;

    img {
      width: 100%;
    }
  }

  .message-sent,
  .message-received {
    .message-bubble {
      -webkit-mask-box-image: none !important;

      &::before {
        display: none;
      }
    }
  }

  .message-sent {
    margin-right: calc(18px + var(--f7-safe-area-right));

    .message-content {
      margin-left: 18px;
    }

    .message-bubble {
      box-shadow: 0 2px 4px 0 rgba(210, 210, 210, 0.5);
      border-radius: var(--f7-message-bubble-border-radius) 0 0
        var(--f7-message-bubble-border-radius);
    }

    .message-image:last-child img {
      border-radius: var(--f7-message-bubble-border-radius) 0 0
        var(--f7-message-bubble-border-radius);
    }

    .file-preview {
      border-radius: var(--f7-message-bubble-border-radius) 0 0
        var(--f7-message-bubble-border-radius);
    }

    &.message-first {
      .message-bubble {
        border-radius: var(--f7-message-bubble-border-radius) var(--f7-message-bubble-border-radius)
          0 var(--f7-message-bubble-border-radius);
      }

      .message-image:last-child img {
        border-radius: var(--f7-message-bubble-border-radius) var(--f7-message-bubble-border-radius)
          0 var(--f7-message-bubble-border-radius);
      }

      .file-preview {
        border-radius: var(--f7-message-bubble-border-radius) var(--f7-message-bubble-border-radius)
          0 var(--f7-message-bubble-border-radius);
      }
    }

    &.message-tail {
      .message-bubble {
        border-radius: var(--f7-message-bubble-border-radius) 0
          var(--f7-message-bubble-border-radius) var(--f7-message-bubble-border-radius);

        .message-image:last-child img {
          border-radius: var(--f7-message-bubble-border-radius) 0
            var(--f7-message-bubble-border-radius) var(--f7-message-bubble-border-radius);
        }
      }

      .file-preview {
        border-radius: var(--f7-message-bubble-border-radius) 0
          var(--f7-message-bubble-border-radius) var(--f7-message-bubble-border-radius);
      }
    }

    .question-button {
      max-width: 250px;

      img {
        width: 100%;
        border-radius: 6px;
      }

      a.button.button-fill {
        margin-bottom: 15px;

        --f7-button-bg-color: var(--f7-theme-color-tint) !important;

        p {
          margin: 0;
        }
      }
    }
  }

  .message-received {
    margin-left: calc(18px + var(--f7-safe-area-left));

    .message-avatar {
      margin-right: 10px;
      align-self: flex-start;
      // background-color: rgb(238, 248, 255);
      background-color: tomato;
    }

    .message-content {
      margin-right: 18px;
    }

    .message-bubble {
      padding-left: var(--f7-message-bubble-padding-horizontal);
      border-radius: 0 var(--f7-message-bubble-border-radius) var(--f7-message-bubble-border-radius)
        0;
    }

    .message-image {
      margin-left: calc(-1 * var(--f7-message-bubble-padding-horizontal));

      &:last-child img {
        border-radius: 0 var(--f7-message-bubble-border-radius)
          var(--f7-message-bubble-border-radius) 0;
      }
    }

    .file-preview {
      border-radius: 0 var(--f7-message-bubble-border-radius) var(--f7-message-bubble-border-radius)
        0;
    }

    .audio-player {
      .button {
        box-shadow: none;
        background-color: transparent;
      }

      .icon {
        color: #32acf5;
      }
    }

    &.message-first {
      .message-bubble {
        border-radius: var(--f7-message-bubble-border-radius) var(--f7-message-bubble-border-radius)
          var(--f7-message-bubble-border-radius) 0;

        .message-image:last-child img {
          border-radius: var(--f7-message-bubble-border-radius)
            var(--f7-message-bubble-border-radius) var(--f7-message-bubble-border-radius) 0;
        }
      }

      .file-preview {
        border-radius: var(--f7-message-bubble-border-radius) var(--f7-message-bubble-border-radius)
          var(--f7-message-bubble-border-radius) 0;
      }
    }

    &.message-tail:not(.message-typing) {
      .message-bubble {
        border-radius: 0 var(--f7-message-bubble-border-radius)
          var(--f7-message-bubble-border-radius) var(--f7-message-bubble-border-radius);

        .message-image:last-child img {
          border-radius: 0 var(--f7-message-bubble-border-radius)
            var(--f7-message-bubble-border-radius) var(--f7-message-bubble-border-radius);
        }
      }

      .file-preview {
        border-radius: 0 var(--f7-message-bubble-border-radius)
          var(--f7-message-bubble-border-radius) var(--f7-message-bubble-border-radius);
      }
    }
  }

  &-empty-message {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 10;
    font-size: 16px;
    color: #87919c;
  }
}

.chat-info {
  display: flex;
  align-items: center;
  height: var(--f7-navbar-height);

  &-name {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 0 30px;
    font-size: 18px;
    left: -1px;
    border-right: 1px solid #d8d8d8;
    border-left: 1px solid #d8d8d8;
  }

  &-duration {
    margin-left: 30px;
    font-size: 14px;

    b {
      font-weight: 600 !important;
    }
  }
}

.chat-attach-file {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  height: 44px;
  align-self: flex-end;

  input {
    display: none;
  }
}

.messagebar {
  width: calc(100% - 400px) !important;

  textarea {
    &::-webkit-scrollbar {
      display: none !important;
    }
  }

  &.hide-text-area {
    textarea {
      display: none !important;
    }
  }
}

.link.recording {
  .recordVawe {
    position: absolute;
    top: calc(50% - 18px);
    left: calc(50% - 18px);
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: rgba(142, 142, 147, 0.7);
    z-index: -1;
    animation-name: wave;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
    animation-duration: 1.35s;
  }

  i {
    z-index: 2;
  }
}

.message-utils-button.link {
  position: absolute;
  top: 0;
  right: 15px;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
}

.quote-preview {
  padding-bottom: 13px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &-text {
    margin: 0;
    margin-top: 10px;
    font-style: italic;
    font-size: 16px;
    font-weight: 500;
    color: #87919c;
  }

  &-info {
    margin-top: 5px;
    font-weight: 400;
    font-size: 14px;
    color: #2a2a2a;
  }
}

.quote-messages {
  margin-bottom: 15px;
  padding-bottom: 5px;
  text-align: left;
  border-bottom: 1.5px solid rgba(#ffffff, 0.5);

  &-text {
    margin-top: 5px;
    font-style: italic;
    font-size: 16px;
    font-weight: 500;
  }

  &-info {
    margin-top: 5px;
    font-size: 14px;
    color: rgba(#ffffff, 0.6);
  }
}

.quote-preview-close.link {
  position: absolute;
  top: 0;
  right: 0;
  min-height: 0 !important;
}

.messagebar-attachment-add-button {
  width: 70px;
  background-color: rgba(127, 130, 134, 0.7);

  .messagebar-attachment-delete {
    display: none;
  }

  label {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    input {
      display: none;
    }
  }

  .icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}

@keyframes wave {
  0% {
    transform: scale(1);
    opacity: 0.7;
  }

  100% {
    transform: scale(2);
    opacity: 0;
  }
}

.aurora .message-image {
  margin: var(--f7-message-bubble-padding-vertical)
    calc(-1 * var(--f7-message-bubble-padding-horizontal) + 4px) !important;
}

.aurora .message-image:first-child {
  margin-top: calc(-1 * var(--f7-message-bubble-padding-vertical) + 4px) !important;
}

.aurora .message-image:last-child {
  margin-bottom: calc(-1 * var(--f7-message-bubble-padding-vertical) + 4px) !important;
}
</style>
